import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	PageHeader,
	Table,
	Card,
	Icon,
	Modal,
	Typography,
	Input,
	message,
	Button,
	Tag,
	Dropdown,
	Menu,
	DatePicker,
	Select,
	Row,
	Col,
	Calendar,
	Radio,
} from 'antd';

import Meta from '../../../../components/Meta';
import Form from '../../../../components/Form';
import Fallback from '../../../../components/Fallback';
import { Container } from './styles';

import moment from 'moment';
import 'moment/locale/pt-br';
import CalendarEventsApi from '../../../../services/sdks/calendarEvents';

const { Option } = Select;
const { Search } = Input;

const breadcrumb = {
	routes: [
		{ breadcrumbName: 'Início' },
		{ breadcrumbName: 'Agentes de IA' },
		{ breadcrumbName: 'Eventos do Calendário' },
	],
	style: { marginBottom: 12 },
};

const Events = () => {
	moment.updateLocale('pt-br', {
		weekdaysMin: ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'],
		weekdaysShort: ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'],
	});
	moment.locale('pt-br');
	const [fallback, setFallback] = useState({ initialData: true });
	const [events, setEvents] = useState([]);
	const [showUpsertModal, setShowUpsertModal] = useState(false);
	const [event, setEvent] = useState(null);
	const [loading, setLoading] = useState(false);
	const [viewMode, setViewMode] = useState('table'); // "table" | "calendar"

	const [pagination, setPagination] = useState({
		current: 1,
		perPage: 10,
		total: 0,
		pages: 0,
	});

	const [filters, setFilters] = useState({
		title: '',
		date: null,
		category: null,
	});

	const isFormValid = useMemo(() => {
		if (!event?.title || !event?.date || !event?.category) {
			return false;
		}

		return true;
	}, [event]);

	const getCategoryType = useCallback((category) => {
		switch (category) {
			case 'COMMEMORATIVE':
				return 'blue';
			case 'RELIGIOUS':
				return 'purple';
			case 'HOLIDAY':
				return 'green';
			default:
				return 'gray';
		}
	}, []);

	const dateCellRender = useCallback(
		(value) => {
			const dayEvents = events.filter((ev) => ev.date === value.format('DD-MM-YYYY'));
			return (
				<ul style={{ listStyle: 'none', padding: 0, cursor: 'default' }}>
					{dayEvents.map((item) => (
						<li key={item._id} style={{ cursor: 'default', marginBottom: 4 }}>
							<Tag
								color={getCategoryType(item.category)}
								style={{ cursor: 'pointer' }}
								onClick={() => {
									setEvent(item);
									setShowUpsertModal(true);
								}}>
								{item.title}
							</Tag>
						</li>
					))}
				</ul>
			);
		},
		[events, getCategoryType]
	);

	const monthCellRender = useCallback(
		(value) => {
			const monthEvents = events.filter((ev) => {
				const evDate = moment(ev.date, 'DD-MM-YYYY');
				return evDate.year() === value.year() && evDate.month() === value.month();
			});

			return (
				<ul style={{ listStyle: 'none', padding: 0, cursor: 'default' }}>
					{monthEvents.map((item) => (
						<li key={item._id} style={{ cursor: 'default', marginBottom: 4 }}>
							<Tag
								color={getCategoryType(item.category)}
								style={{ cursor: 'pointer' }}
								onClick={() => {
									setEvent(item);
									setShowUpsertModal(true);
								}}>
								{item.title}
							</Tag>
						</li>
					))}
				</ul>
			);
		},
		[events, getCategoryType]
	);

	const columns = [
		{
			title: 'ID',
			dataIndex: '_id',
			key: 'id',
			render: (_id) => (
				<Typography.Text title={_id} copyable={{ text: _id }}>{`${_id.slice(
					0,
					5
				)}...`}</Typography.Text>
			),
		},
		{
			title: 'Título',
			key: 'title',
			dataIndex: 'title',
		},
		{
			title: 'Data',
			key: 'date',
			dataIndex: 'date',
			render: (date) => {
				const [day, month, year] = date.split('-');
				return `${day}/${month}/${year}`;
			},
		},
		{
			title: 'Categoria',
			dataIndex: 'category',
			key: 'category',
			render: (category) => {
				switch (category) {
					case 'COMMEMORATIVE':
						return <Tag color='blue'>Comemorativo</Tag>;
					case 'RELIGIOUS':
						return <Tag color='purple'>Religioso</Tag>;
					case 'HOLIDAY':
						return <Tag color='green'>Feriado</Tag>;
					default:
						return <Tag>{category}</Tag>;
				}
			},
		},
		{
			title: 'Ações',
			key: 'actions',
			align: 'right',
			render: (event) => (
				<Dropdown
					placement='bottomLeft'
					overlay={
						<Menu>
							<Menu.Item
								key='details'
								onClick={() => {
									setEvent(event);
									setShowUpsertModal(true);
								}}>
								<Icon type='edit' /> Editar
							</Menu.Item>

							<Menu.Divider />

							<Menu.Item
								key='delete'
								className='ant-dropdown-menu-item-danger'
								onClick={() => {
									Modal.confirm({
										title: 'Excluir Evento do Calendário?',
										icon: 'exclamation-circle',
										content:
											'Essa ação não poderá ser revertida, realmente deseja excluir esse Evento do Calendário?',
										okText: 'Excluir',
										onOk: () => handleDeleteEvent(event?._id),
										okButtonProps: {
											icon: 'delete',
											type: 'danger',
										},
										cancelText: 'Cancelar',
										cancelButtonProps: {
											icon: 'close-circle',
										},
									});
								}}>
								<Icon type='delete' /> Excluir
							</Menu.Item>
						</Menu>
					}>
					<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 12 }} type='more' />
				</Dropdown>
			),
		},
	];

	const fetchInitialData = useCallback(
		async (page = 1, perPage = pagination.perPage, searchFilters = {}) => {
			try {
				setLoading(true);

				const params = { page, perPage };

				if (searchFilters.title) {
					params.title = searchFilters.title;
				}
				if (searchFilters.date) {
					params.date = searchFilters.date;
				}
				if (searchFilters.category) {
					params.category = searchFilters.category;
				}

				const response = await CalendarEventsApi.getAllCalendarEvents(params);
				const { events, pagination: apiPagination } = response.data;

				setEvents(events);

				setPagination({
					current: page,
					perPage: perPage,
					total: apiPagination?.total || 0,
					pages: apiPagination?.pages || 0,
				});
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os eventos, tente novamente');
				setEvents([]);
				setPagination({
					current: 1,
					perPage: pagination.perPage,
					total: 0,
					pages: 0,
				});
			} finally {
				setLoading(false);
				setFallback((prev) => ({ ...prev, initialData: false }));
			}
		},
		[pagination.perPage]
	);

	const handleDeleteEvent = useCallback(
		async (eventId) => {
			try {
				await CalendarEventsApi.deleteCalendarEvent(eventId);

				fetchInitialData(pagination.current, pagination.perPage);

				return message.success('Evento excluído do Calendário com sucesso');
			} catch (error) {
				console.error(error);
				message.error('Houve um erro, tente novamente');
			}
		},
		[fetchInitialData, pagination]
	);

	const handleUpsertEvent = useCallback(async () => {
		setFallback((prev) => ({ ...prev, upsert: true }));

		try {
			const isUpdate = !!event?._id;
			const payload = {
				title: event.title,
				date: event.date,
				category: event.category,
			};

			if (isUpdate) {
				await CalendarEventsApi.updateCalendarEvent(event._id, payload);
				fetchInitialData(pagination.current, pagination.perPage, filters);
			} else {
				await CalendarEventsApi.createCalendarEvent(payload);
				fetchInitialData(1, pagination.perPage, filters);
			}

			setEvent(null);
			setShowUpsertModal(false);
			message.success('Evento criado/atualizado com sucesso');
		} catch (error) {
			message.error('Erro ao criar/atualizar o evento');
		}

		setFallback((prev) => ({ ...prev, upsert: false }));
	}, [
		event?._id,
		event?.title,
		event?.date,
		event?.category,
		fetchInitialData,
		pagination,
		filters,
	]);

	useEffect(() => {
		const newPerPage = viewMode === 'calendar' ? 100000 : 10;
		setPagination((prev) => ({ ...prev, perPage: newPerPage }));
		fetchInitialData(1, newPerPage);
	}, [fetchInitialData, viewMode]);

	const handlePageChange = useCallback(
		(page, perPage) => {
			fetchInitialData(page, perPage, filters);
		},
		[fetchInitialData, filters]
	);

	const handleSearch = useCallback(
		(value) => {
			const newFilters = { ...filters, title: value };
			setFilters(newFilters);
			fetchInitialData(1, pagination.perPage, newFilters);
		},
		[filters, pagination.perPage, fetchInitialData]
	);

	const handleDateFilter = useCallback(
		(date) => {
			const dateString = date ? date.format('DD-MM-YYYY') : null;
			const newFilters = { ...filters, date: dateString };
			setFilters(newFilters);
			fetchInitialData(1, pagination.perPage, newFilters);
		},
		[filters, pagination.perPage, fetchInitialData]
	);

	const handleCategoryFilter = useCallback(
		(category) => {
			const newFilters = { ...filters, category };
			setFilters(newFilters);
			fetchInitialData(1, pagination.perPage, newFilters);
		},
		[filters, pagination.perPage, fetchInitialData]
	);

	const handleClearFilters = useCallback(() => {
		const clearedFilters = { title: '', date: null, category: null };
		setFilters(clearedFilters);
		fetchInitialData(1, pagination.perPage, clearedFilters);
	}, [pagination.perPage, fetchInitialData]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Eventos do Calendário' />

			<PageHeader
				title='Eventos do Calendário'
				breadcrumb={breadcrumb}
				extra={[
					<Button
						key='create-banner'
						type='ghost'
						size='large'
						icon='plus-circle'
						onClick={() => {
							setShowUpsertModal(true);
							setEvent({});
						}}>
						Novo Evento
					</Button>,
				]}>
				<Typography.Text>Eventos do Calendário adicionados pelo ADMIN</Typography.Text>
			</PageHeader>

			<Container>
				<Card style={{ marginBottom: 16 }}>
					<Row gutter={20} align='middle'>
						<Col xs={24} sm={8} md={4}>
							<Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
								<Radio.Button value='table'>Tabela</Radio.Button>
								<Radio.Button value='calendar'>Calendário</Radio.Button>
							</Radio.Group>
						</Col>
						<Col xs={24} sm={8} md={4}>
							<Search
								placeholder='Buscar por título'
								allowClear
								onSearch={handleSearch}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} sm={8} md={4}>
							<DatePicker
								placeholder='Filtrar por data'
								format='DD/MM/YYYY'
								value={filters.date ? moment(filters.date, 'DD-MM-YYYY') : null}
								onChange={handleDateFilter}
								allowClear
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} sm={8} md={4}>
							<Select
								placeholder='Filtrar por categoria'
								value={filters.category === null ? undefined : filters.category}
								onChange={handleCategoryFilter}
								allowClear
								style={{ width: '100%' }}>
								<Option value='COMMEMORATIVE'>Comemorativo</Option>
								<Option value='RELIGIOUS'>Religioso</Option>
								<Option value='HOLIDAY'>Feriado</Option>
							</Select>
						</Col>
						<Col xs={24} sm={8} md={4}>
							<Button icon='reload' onClick={handleClearFilters} style={{ width: '100%' }}>
								Limpar Filtros
							</Button>
						</Col>
					</Row>
				</Card>

				{viewMode === 'table' ? (
					<Card>
						<Table
							size='middle'
							rowKey='_id'
							columns={columns}
							dataSource={events}
							loading={loading}
							style={{ border: 'none' }}
							pagination={{
								size: 'large',
								pageSize: pagination.perPage,
								hideOnSinglePage: true,
								current: pagination.current,
								total: pagination.total,
								onChange: handlePageChange,
								onShowSizeChange: handlePageChange,
								showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
							}}
						/>
					</Card>
				) : (
					<Card>
						<Calendar
							dateCellRender={dateCellRender}
							monthCellRender={monthCellRender}
							style={{ cursor: 'default' }}
						/>
					</Card>
				)}
			</Container>

			<Modal
				title={
					<>
						<Icon type='file-text' style={{ marginRight: 8 }} /> {event?._id ? 'Editar' : 'Criar'}{' '}
						Evento do Calendário
					</>
				}
				width={600}
				visible={showUpsertModal}
				okButtonProps={{ disabled: !isFormValid, loading: fallback?.upsert }}
				okText={`${event?._id ? 'Editar' : 'Criar'} Evento`}
				onOk={handleUpsertEvent}
				onCancel={() => {
					setShowUpsertModal(false);
					setEvent(null);
				}}>
				<Form.Container layout='1fr'>
					<Form.Item label='Título'>
						<Input
							placeholder='Informe o título do evento'
							value={event?.title}
							onChange={(e) => setEvent({ ...event, title: e.target.value })}
						/>
					</Form.Item>
					<Form.Item label='Data do Evento'>
						<DatePicker
							placeholder='Data do Evento'
							format='DD/MM/YYYY'
							value={event?.date ? moment(event.date, 'DD-MM-YYYY') : null}
							onChange={(d) =>
								setEvent({
									...event,
									date: d ? d.format('DD-MM-YYYY') : null,
								})
							}
						/>
					</Form.Item>

					<Form.Item label='Categoria'>
						<select
							value={event?.category || ''}
							onChange={(e) => setEvent({ ...event, category: e.target.value })}
							style={{
								width: '100%',
								padding: '8px',
								borderRadius: '4px',
								border: '1px solid #d9d9d9',
							}}>
							<option value='' disabled>
								Selecione uma categoria
							</option>
							<option value='COMMEMORATIVE'>Comemorativo</option>
							<option value='RELIGIOUS'>Religioso</option>
							<option value='HOLIDAY'>Feriado</option>
						</select>
					</Form.Item>
				</Form.Container>
			</Modal>
		</>
	);
};

export default Events;
